import { create } from 'zustand'
import { bridge } from '../bridge'
// Type-only import (erased at build): the canonical Proposal shape lives with the
// review UI; the store just buckets arrays of them per household.
import type { Proposal as ProvisioningProposal } from '../screens/agents/onboardingReview'
import type {
  AgentInstance,
  Announcement,
  AttentionRoom,
  AttentionThread,
  AttentionSuggestion,
  ChatMessage,
  ChatNotifMode,
  ChatRoom,
  ConnectionStatus,
  HistoryItem,
  AgentJobDescription,
  DisplayScreen,
  Host,
  IODevice,
  InviteSummary,
  MyOrganization,
  PendingInvite,
  OrganizationMember,
  WorkingDirectory,
  OrganizationPosition,
  InitiatorNotice,
  PermissionRequest,
  PluginInstall,
  ResourceConnection,
  Rule,
  ScheduledTrigger,
  Secret,
  Toast,
  TranscriptEntry,
} from './types'

// Shared empty array for households with no bucket yet. Returning this
// exact reference (rather than a fresh []) keeps the projection
// referentially stable, which zustand's default equality depends on:
// subscribers like the deferred push-tap resolver in api/ws.ts compare
// `s.agentInstances === prev.agentInstances`, and a new array on every
// set() would fire them on unrelated state changes forever.
const EMPTY: never[] = []

// Household-scoped slices, as [projection key, storage key] pairs. One
// list here is the whole contract — reproject() and clearHouseholdState()
// both drive off it, so adding a tier-2 list means adding one entry.
const HOUSEHOLD_SLICES = [
  ['agentInstances', 'agentInstancesByOrg'],
  ['organizationPositions', 'organizationPositionsByOrg'],
  ['hosts', 'hostsByOrg'],
  ['rules', 'rulesByOrg'],
  ['chatRooms', 'chatRoomsByOrg'],
  ['scheduledTriggers', 'scheduledTriggersByOrg'],
  ['provisioningProposals', 'provisioningProposalsByOrg'],
] as const

type HouseholdProjection = Partial<Pick<AppState, (typeof HOUSEHOLD_SLICES)[number][0]>>

// reproject recomputes the current-household view from the buckets.
// Returns only the slices whose reference actually changed, so an
// unrelated set() can't invalidate a projection that didn't move.
function reproject(state: AppState): HouseholdProjection {
  const orgID = state.organizationID
  const next: Record<string, unknown> = {}
  for (const [viewKey, storeKey] of HOUSEHOLD_SLICES) {
    const bucket = (orgID ? state[storeKey][orgID] : undefined) ?? EMPTY
    if (bucket !== state[viewKey]) next[viewKey] = bucket
  }
  return next as HouseholdProjection
}

export type Sheet =
  | { kind: 'paywall'; upgradeOnly?: boolean }
  | { kind: 'household_picker'; initialMode?: 'list' | 'create' }
  | { kind: 'invitations' }
  | { kind: 'voice_devices' }
  | { kind: 'developer' }
  | { kind: 'resource_connections_debug' }
  | { kind: 'hosts_debug' }
  | { kind: 'devices_debug' }
  | { kind: 'positions_debug' }
  | { kind: 'working_directories_debug' }
  | { kind: 'organization_members_debug' }
  | { kind: 'agent_job_descriptions_debug' }
  | { kind: 'secrets_debug' }
  | { kind: 'plugin_installs_debug' }
  | { kind: 'rules_debug' }
  | { kind: 'approvers' }
  | { kind: 'notification_preferences' }
  | { kind: 'attention' }
  | { kind: 'open_requests' }
  | { kind: 'delegations' }
  | { kind: 'chat_settings'; roomID: string; roomName: string }
  | { kind: 'create_chat_room' }
  | { kind: 'create_entity' }
  | { kind: 'connect_account' }
  | { kind: 'blueprint_picker' }
  | { kind: 'delete_account' }
  | { kind: 'delete_household'; organizationID: string }

/// Navigation stack entries. Pushed screens slide in from the right like
/// UINavigationController; pop returns. Separate from `sheets` which are
/// modal bottom-rising cards.
// One household's line in the attention summary (list_attention): what's
// waiting there, for the household switcher's chip.
// unread_threads is optional: a server predating threads doesn't send it.
export type HouseholdAttention = {
  asks: number
  unread_rooms: number
  unread_threads?: number
  // Setup suggestions wanting a decision.
  suggestions?: number
}

// The in-app badge — the same inputs the server sums for the app icon
// (badgeCount in relay badge_count.go): asks + unread rooms + unread threads +
// invitations + setup suggestions wanting a decision. The one place the client
// adds them up, so the bell and the icon can't drift apart. `asks` is passed in
// because callers settle it from different sources (the live pending set, or
// the canonical list).
export function attentionBadgeTotal(s: AppState, asks: number): number {
  return (
    asks + s.unreadRoomCount + s.unreadThreadCount + s.myInvites.length + s.pendingSuggestionCount
  )
}

export type NavEntry =
  | {
      kind: 'agent_detail'
      agentInstanceID: string
      positionID: string
      // Marker so AnnouncementOverlay can suppress while the user is on
      // an agent_detail they reached via push tap. Manual nav doesn't
      // set this; only resolvePushNavigation does.
      source?: 'push'
    }
  | { kind: 'new_agent_wizard' }
  | {
      kind: 'agent_mandate'
      title: string
      mandate: string
      // Present → the mandate is editable (saving it can trigger the Facilitator
      // to propose a capability delta for the role). Absent → read-only.
      jobDescriptionID?: string
      requiredSkills?: string
    }
  | {
      kind: 'agent_settings'
      agentInstanceID: string
      positionID: string
      agentName: string
      hostname: string
    }
  | { kind: 'agent_grants'; positionID: string }
  | { kind: 'agent_resources'; agentInstanceID: string }
  | { kind: 'chat_room'; roomID: string; roomName: string }
  // A thread, full screen over its room (Back returns to the room). focus opens
  // it with the composer focused (swipe-right / "Reply in thread").
  | { kind: 'chat_thread'; roomID: string; roomName: string; rootID: string; focus?: boolean }
  | { kind: 'routines' }
  | { kind: 'routine_editor'; triggerID?: string }
  | {
      kind: 'onboarding_review'
      proposalID: string
      // Present when reviewing a NEW hire's bundle (finish → that agent's detail).
      // Absent for the resource→jobs sweep (finish → just return to the previous
      // screen).
      agentInstanceID?: string
      positionID?: string
    }

export type AppState = {
  // Auth / registration
  isRegistered: boolean
  humanUserID: string | null
  organizationID: string | null
  ioDeviceID: string | null
  email: string | null
  // Never surface the secret to UI code; keep it in closure via api client.
  // Why the last sign-out happened, so RegistrationScreen can distinguish
  // "you were signed out because this device's access was revoked" from a
  // plain first launch. 'revoked' is set when the server rejects our device
  // credentials (remote unenroll); null otherwise. Cleared on re-register.
  sessionEndedReason: 'revoked' | null

  // Subscription
  isSubscribed: boolean
  // Product ID and platform of the caller's own active subscription, if any.
  // Fetched from list_my_pro_subscriptions after each WS connect; null while
  // loading or when the org is Pro via a legacy org-level offer code only.
  // platform: 'apple' | 'offer'
  subscribedProductID: string | null
  subscribedPlatform: string | null
  subscribedExpiresAt: string | null
  hasActiveOffer: boolean
  // Last server response for an offer-code redemption attempt. Set from
  // offer_status frames with status != "active". null in idle state.
  offerError: string | null

  // Connection
  connectionStatus: ConnectionStatus

  // ---- Household-scoped lists (tier 2) --------------------------------
  //
  // Stored per household, rendered one household at a time. The `*ByOrg`
  // records are the storage; the five bare arrays below are a projection
  // of whichever household `organizationID` names.
  //
  // Screens read ONLY the projection. Nothing outside this file should
  // touch a `*ByOrg` record — see docs/state-replication-edges.md for why
  // the two layers exist and what belongs in each tier.
  //
  // Keying by household is what makes a frame for a household the device
  // isn't looking at *inert* rather than *corrupting*: it lands in its own
  // bucket and no selector reads it. Before this, every list was applied
  // wholesale and a stray cross-household push silently replaced the view.
  agentInstancesByOrg: Record<string, AgentInstance[]>
  organizationPositionsByOrg: Record<string, OrganizationPosition[]>
  hostsByOrg: Record<string, Host[]>
  rulesByOrg: Record<string, Rule[]>
  chatRoomsByOrg: Record<string, ChatRoom[]>
  scheduledTriggersByOrg: Record<string, ScheduledTrigger[]>
  provisioningProposalsByOrg: Record<string, ProvisioningProposal[]>

  // Projection of the current household. Written ONLY by reproject().
  agentInstances: AgentInstance[]
  organizationPositions: OrganizationPosition[]
  hosts: Host[]
  rules: Rule[]
  scheduledTriggers: ScheduledTrigger[]
  provisioningProposals: ProvisioningProposal[]
  availableOrganizations: MyOrganization[]
  // True after the first my_organizations payload has been
  // received. Lets the router distinguish "still loading" from
  // "loaded and empty" — orphaned-user UX gates on the latter, so
  // we don't want to flash the OrphanedScreen during reconnect.
  hasLoadedOrganizations: boolean
  // Server-computed cap on how many orgs this user may own. Tier-based:
  // free/pro_monthly = 1, pro_multi_home_monthly = server-configured N.
  // 0 means unlimited (dev / limits not configured).
  ownedOrgCap: number
  // Phase 2: server-stored Resource Connections + the plugin
  // registry that connections FK into. Both are pulled on demand
  // by the temporary CRUD view; not subscribed to live updates.
  resourceConnections: ResourceConnection[]
  pluginInstalls: PluginInstall[]
  // Owner-scoped device list. Filled on-demand by the debug devices
  // sheet via list_my_io_devices; not live-pushed.
  ioDevices: IODevice[]
  // Household display screens enriched with serving host + what's-showing +
  // health. Filled on-demand by the Devices → Displays segment via
  // list_display_screens (§B6); not live-pushed.
  displayScreens: DisplayScreen[]
  // Org-scoped working directories. Filled on-demand by the debug
  // working-directories sheet via list_working_directories.
  workingDirectories: WorkingDirectory[]
  // Org-scoped members + JDs. Filled on-demand by their debug sheets.
  organizationMembers: OrganizationMember[]
  invites: InviteSummary[]
  // Invitations addressed to THIS user (my_invites). Pushed on every
  // /ws connect and after any accept/decline/block, so it stays correct
  // across a user's devices without polling.
  myInvites: PendingInvite[]
  agentJobDescriptions: AgentJobDescription[]
  secrets: Secret[]
  toasts: Toast[]

  // Org chat
  chatRooms: ChatRoom[]
  // Messages keyed by room_id; deduped by id and kept sorted chronologically
  // (timestamp, then id) so a live message that races the initial history
  // page can't wedge itself out of order — see chatMsgLess.
  chatMessages: Record<string, ChatMessage[]>
  // Unread count per room. Incremented on WS-pushed messages when the room is
  // not open; cleared to 0 on ChatRoomScreen mount. NOT reset on reconnect —
  // it's a client-side ephemeral counter and chat_rooms_list replays on every
  // connect, so resetting there wiped genuinely-unread badges (W7).
  chatUnreadCounts: Record<string, number>
  // Cached notification mode per room. Loaded on ChatSettingsSheet open.
  chatNotifModes: Record<string, ChatNotifMode>

  // Per-household attention counts (asks + unread rooms), keyed by org id, for
  // the household switcher's chips — the cross-household "B wants you while
  // you're in A" signal. Server-computed (list_attention); refreshed on
  // connect, on foreground, and when the switcher opens. A household absent
  // from the map has nothing waiting. See docs/badge-unread-messages-plan.md
  // and the Attention Inbox proposal.
  attentionByOrg: Record<string, HouseholdAttention>
  setAttentionByOrg: (m: Record<string, HouseholdAttention>) => void
  // Cross-household unread chat rooms with detail (name, sender, preview, time)
  // — the chat rows the Attention Inbox renders. Loaded when the inbox opens
  // (list_attention_rooms); not a live-pushed list. See AttentionSheet.
  attentionRooms: AttentionRoom[]
  setAttentionRooms: (rooms: AttentionRoom[]) => void
  // Followed threads with unread replies, cross-household — the inbox's thread
  // rows. Loaded with attentionRooms (same list_attention_rooms response).
  attentionThreads: AttentionThread[]
  setAttentionThreads: (threads: AttentionThread[]) => void
  // Open setup suggestions, cross-household — the inbox's suggestion rows.
  // Loaded with attentionRooms (same list_attention_rooms response).
  attentionSuggestions: AttentionSuggestion[]
  setAttentionSuggestions: (items: AttentionSuggestion[]) => void

  // Permission / transcript / history
  pendingRequests: PermissionRequest[]
  // The non-ask half of the app-icon badge: the number of chat ROOMS with
  // unread messages for this human. Server-computed — read state is per-human
  // and lives in the DB, so the client cannot derive this. Refreshed from
  // pending_approvals_list and from marking a room read; deliberately NOT
  // derived locally from chatUnreadCounts, because a count that drifts is worse
  // than one that is briefly stale. (chatUnreadCounts feeds the per-room pips;
  // this feeds the one app-icon integer, which counts places, not messages.)
  unreadRoomCount: number
  setUnreadRoomCount: (n: number) => void
  // The thread half: followed threads with replies you haven't read. Plain
  // thread replies don't make a ROOM unread, so this is how they reach the
  // badge. Server-computed and refreshed from the same responses as
  // unreadRoomCount, plus live chat_unread_counts frames. See
  // docs/chat-threads-plan.md §5.
  unreadThreadCount: number
  setUnreadThreadCount: (n: number) => void
  // Setup suggestions wanting a decision, across households — the badge's
  // suggestions input. Refreshed from pending_approvals_list and live
  // attention_counts frames.
  pendingSuggestionCount: number
  setPendingSuggestionCount: (n: number) => void
  // Transcript entries bucketed by ai_agent_instance_id. Each bucket is
  // deduped by entry id and pruned to the newest ~200 INDEPENDENTLY — so one
  // agent's history replay (up to ~500 entries) can't evict, and a scoped
  // clear_transcript can't wipe, another agent's transcript. Read a bucket
  // with `transcripts[agentInstanceID] ?? []`.
  transcripts: Record<string, TranscriptEntry[]>
  // The agent currently in a hands-free voice session, or null. At most one at
  // a time and pinned to the agent whose screen it was opened from — the same
  // "one session, one agent" rule the Home Assistant path follows. Lives here
  // rather than in component state because two places need it: the session bar
  // that speaks replies, and the input bar that must mark what it sends as
  // spoken. Deliberately NOT persisted: a session is a thing you are doing
  // right now, and restoring one on launch would put a live microphone in front
  // of someone who did not ask for it.
  voiceSessionAgentID: string | null
  setVoiceSessionAgent: (agentInstanceID: string | null) => void
  positionHistory: Record<string, HistoryItem[]>
  // Tracks which positionIDs have had an initial history load (fresh load or
  // reconnect gap-fill). Prevents HistorySegment from firing repeated fetches
  // on re-render. Keyed by positionID.
  historyLoadedFor: Record<string, boolean>

  // UI
  sheets: Sheet[]
  navStack: NavEntry[]
  selectedAgentInstanceID: string | null
  // Per-agent draft text. Keyed by ai_agent_instance_id so navigating
  // between agents preserves whatever was being typed in each. In-memory
  // only — drafts don't survive app restart by design.
  relayInputDrafts: Record<string, string>
  // Debug toggle (localStorage-backed, default off): when true the transcript
  // renders agent-directed system FYIs (resource granted/revoked/plugin
  // updated) and the agent's <hearth-silent/> non-response markers, which are
  // normally hidden as noise. Toggled from the Developer Area.
  showSystemMessages: boolean

  // Actions
  setRegistered: (r: {
    humanUserID: string
    organizationID: string
    ioDeviceID: string
    email: string | null
  }) => void
  clearRegistration: () => void
  setSessionEndedReason: (r: 'revoked' | null) => void
  setShowSystemMessages: (v: boolean) => void
  setConnectionStatus: (s: ConnectionStatus) => void
  setSubscribed: (v: boolean) => void
  setSubscribedProductID: (id: string | null) => void
  setSubscribedPlatform: (p: string | null) => void
  setSubscribedExpiresAt: (d: string | null) => void
  setActiveOffer: (v: boolean) => void
  setOfferError: (err: string | null) => void

  // Household-scoped setters. orgID names the household the list
  // describes — pass the frame's organization_id, NOT the current
  // household, so a frame that arrives for another household is filed
  // rather than applied. Callers that genuinely mean "the current one"
  // should pass state.organizationID explicitly.
  replaceAgentInstances: (orgID: string, a: AgentInstance[]) => void
  replacePositions: (orgID: string, p: OrganizationPosition[]) => void
  replaceHosts: (orgID: string, h: Host[]) => void
  replaceRules: (orgID: string, r: Rule[]) => void
  // Drops every household-scoped bucket. Sign-out only.
  clearHouseholdState: () => void
  replaceOrganizations: (o: MyOrganization[], ownedOrgCap?: number) => void
  replaceResourceConnections: (c: ResourceConnection[]) => void
  replacePluginInstalls: (p: PluginInstall[]) => void
  replaceIODevices: (d: IODevice[]) => void
  replaceDisplayScreens: (d: DisplayScreen[]) => void
  replaceWorkingDirectories: (w: WorkingDirectory[]) => void
  replaceOrganizationMembers: (m: OrganizationMember[]) => void
  replaceInvites: (i: InviteSummary[]) => void
  replaceMyInvites: (i: PendingInvite[]) => void
  replaceAgentJobDescriptions: (j: AgentJobDescription[]) => void
  replaceSecrets: (s: Secret[]) => void
  replaceChatRooms: (orgID: string, rooms: ChatRoom[]) => void
  replaceScheduledTriggers: (orgID: string, t: ScheduledTrigger[]) => void
  replaceProvisioningProposals: (orgID: string, proposals: ProvisioningProposal[]) => void
  upsertProvisioningProposal: (orgID: string, proposal: ProvisioningProposal) => void
  appendChatMessages: (roomID: string, msgs: ChatMessage[]) => void
  // File a history page the server just sent: new rows are added AND rows
  // already held are replaced by the server's copy. A reload after being away
  // (backgrounded, reconnected) must refresh what's on screen, not only add to
  // it: a thread's first message carries its reply summary, and edits,
  // reactions and deletes change rows we already hold. appendChatMessages keeps
  // the held copy, which is what left a new thread reply invisible until restart.
  upsertChatMessages: (roomID: string, msgs: ChatMessage[]) => void
  prependChatMessages: (roomID: string, msgs: ChatMessage[]) => void
  updateChatMessage: (roomID: string, msgID: string, patch: Partial<ChatMessage>) => void
  // Swap an optimistic (local-…) bubble for the authoritative server row once the
  // relay acks the send. Race-safe against the server's own broadcast of the same
  // message: if the broadcast already inserted the real row (matched by real id),
  // this just drops the placeholder; otherwise it replaces the placeholder in
  // place. Either ordering converges to exactly one row.
  confirmOptimisticMessage: (roomID: string, tempID: string, real: ChatMessage) => void
  // Discard a single message row by id. Used to drop a failed optimistic bubble
  // when the user taps Delete (the row never reached the server, so there is
  // nothing to unsend).
  removeChatMessage: (roomID: string, msgID: string) => void
  incrementChatUnread: (roomID: string) => void
  clearChatUnread: (roomID: string) => void
  resetChatUnreadCounts: () => void
  setChatNotifMode: (roomID: string, mode: ChatNotifMode) => void
  pushToast: (text: string, kind?: Toast['kind']) => void
  dismissToast: (id: string) => void

  enqueueRequest: (r: PermissionRequest) => void
  dequeueRequest: (id: string) => void
  // Drop every pending request whose id isn't in the given set. The
  // canonical-list refresh uses it to prune asks resolved while this device
  // wasn't listening; see refreshPendingApprovals.
  retainPendingRequests: (ids: string[]) => void

  // Initiator-informational notices (X2): passive "pending — X is handling"
  // surface for actions the local user triggered that are gated on someone
  // else. upsert adds/refreshes a pending notice (in place, as the ladder
  // escalates); settle marks the disposition when it resolves; dismiss removes.
  initiatorNotices: InitiatorNotice[]
  upsertInitiatorNotice: (n: InitiatorNotice) => void
  settleInitiatorNotice: (requestId: string, status: InitiatorNotice['status'], body: string) => void
  dismissInitiatorNotice: (requestId: string) => void

  appendTranscript: (entries: TranscriptEntry[]) => void
  // Patch one transcript entry in place, by id. Used to reconcile an optimistic
  // bubble with the relay's ack (attaching the mid, or flipping it to failed).
  // No-op when the entry is gone — the real turn may have already landed and
  // displaced it.
  patchTranscriptEntry: (
    aiAgentInstanceID: string,
    id: string,
    patch: Partial<TranscriptEntry>,
  ) => void
  // Mark the message with this mid as never delivered. Addressed by mid rather
  // than entry id because the row may be either the sender's own optimistic
  // bubble or a relay-sent queued_user entry, and the mid is the one identifier
  // both carry.
  markTranscriptUndeliverable: (
    aiAgentInstanceID: string,
    mid: string,
    reason: string,
  ) => void
  // Clear one agent's transcript bucket, or ALL of them when called with no
  // argument (logout). The clear_transcript WS frame carries the agent id, so
  // pass it through — a reconnect clear for one agent must not touch others.
  clearTranscript: (agentInstanceID?: string) => void

  appendHistory: (item: HistoryItem) => void
  // Merge a batch of items (from server query response) into positionHistory,
  // deduplicating by id. Used on initial load and reconnect gap-fills.
  mergeHistory: (positionID: string, items: HistoryItem[]) => void
  markHistoryLoaded: (positionID: string) => void

  pushSheet: (s: Sheet) => void
  popSheet: () => void
  pushNav: (n: NavEntry) => void
  popNav: () => void
  setSelectedAgentInstance: (id: string | null) => void
  setRelayInputDraft: (aiAgentInstanceID: string, t: string) => void

  // Household default temp agent recipe. Fetched after WS connect. Null
  // means not yet loaded; an object with all-null fields means no defaults set.
  tempAgentDefaults: { host_id: string | null; harness_id: string | null; ai_brain_model_id: string | null; harness_name: string | null } | null
  setTempAgentDefaultsStore: (d: { host_id: string | null; harness_id: string | null; ai_brain_model_id: string | null; harness_name: string | null } | null) => void

  pendingTempSpawn: boolean
  tempSpawnError: string | null
  markPendingTempSpawn: () => void
  clearPendingTempSpawn: () => void
  setTempSpawnError: (err: string | null) => void

  // Position id from a push.tapped event whose agent wasn't yet in
  // agentInstances when the tap fired (cold launch / app resume races
  // the WS reconnect + agent_instances_list reply). Cleared either by
  // the ws.ts dispatcher when a matching agent finally lands, or by an
  // explicit clear (manual nav, second push, etc.).
  pendingPushPositionID: string | null
  setPendingPushPositionID: (id: string | null) => void

  // Set when the user opens an invitation deep link. It is a bare
  // "show me my invitations" signal, NOT a credential — invitations are
  // looked up server-side from the signed-in user's verified email, so
  // there is nothing to carry, match, or lose. Cleared once the
  // invitations sheet has been opened.
  invitationsLinkPending: boolean
  setInvitationsLinkPending: (v: boolean) => void
  // A display pairing code from a scanned kiosk QR (/pair?code=…), claimed once the
  // user is enrolled. Cleared after the claim is attempted.
  pendingPairCode: string | null
  setPendingPairCode: (v: string | null) => void
  // The invited address carried on an invitation deep link
  // (/invitations?email=<addr>), used ONLY to prefill the registration
  // email field so an invitee doesn't retype what the link already names.
  // Not a credential and not a gate — the OTP still proves ownership, and
  // every consumption path re-checks the verified email. Cleared once the
  // registration screen has seeded its input from it.
  invitePrefillEmail: string | null
  setInvitePrefillEmail: (email: string | null) => void

  // Request id of the single ask currently shown as an overlay card.
  // Set by:
  //   - push.tapped (with requestID) — user explicitly invoked this ask
  //   - app foreground refresh — set to the newest pending that wasn't
  //     in the device's pending set when the app was backgrounded; on
  //     cold launch the snapshot is empty so this fires for the newest
  //     pending at all (per the "show one ask on app open" UX call)
  // Cleared by:
  //   - User responds to the ask (allow / deny / etc.) — normal flow
  //   - User taps the X dismiss — request stays in queue for the
  //     banner, overlay just hides
  // PermissionOverlay reads it; renders the matching pending or null.
  singleAskRequestID: string | null
  setSingleAskRequestID: (id: string | null) => void

  // Announcements queue. Populated by the WS `announcements` (batch on
  // hello) and `announcement` (single, server-broadcast) frames.
  // AnnouncementOverlay reads queue[0] and applies the suppression gate.
  announcements: Announcement[]
  // Wall-clock ms of the most recent push.tapped/push.received. The
  // overlay's 500ms race debounce uses this to bridge the window
  // between hello-time announcement delivery and a push.tapped that
  // arrives a beat later — without it, an announcement could splash
  // before the permission overlay mounts.
  lastPushEventAt: number | null
  setAnnouncements: (a: Announcement[]) => void
  enqueueAnnouncement: (a: Announcement) => void
  removeAnnouncement: (id: string) => void
  markPushEvent: () => void
}

const TRANSCRIPT_MAX = 200

// Conversation order for chat messages. `seq` is the server's rowid — a
// monotonic per-insert integer that is exact, total, and tie-free, and is
// identical whether a message arrived via live broadcast or the history page,
// so live/replay interleave into one correct order regardless of arrival
// timing. Sort by it. The old scheme sorted by `timestamp` (ms from the
// whole-second created_at), which broke two ways: on iOS WKWebView Date.parse
// rejects the space-separated created_at → Date.now() fallback → order
// collapsed to arrival order; and even when it parsed, same-second messages
// tied and fell back to a UUID compare (random order). Fall back to
// (timestamp, id) only for legacy messages that predate `seq` on the wire.
function chatMsgLess(a: ChatMessage, b: ChatMessage): number {
  if (a.seq != null && b.seq != null) {
    if (a.seq !== b.seq) return a.seq - b.seq
    return 0
  }
  if (a.timestamp !== b.timestamp) return a.timestamp - b.timestamp
  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0
}

// Conversation order for stamped transcript entries: (seq, sub, block) — source
// line index, host emit index, relay block index. Total and monotonic, and
// identical for the same entry whether it arrived live or via replay, so
// live/replay interleave into one correct order. See
// docs/transcript-sequence-number-spec.md.
function transcriptSeqLess(a: TranscriptEntry, b: TranscriptEntry): number {
  if (a.seq !== b.seq) return (a.seq ?? 0) - (b.seq ?? 0)
  if (a.sub !== b.sub) return (a.sub ?? 0) - (b.sub ?? 0)
  return (a.block ?? 0) - (b.block ?? 0)
}

// Debug toggle persisted in localStorage; read once at store init, written by
// setShowSystemMessages. Default off — the FYIs/markers are noise for humans.
const SHOW_SYSTEM_MESSAGES_KEY = 'hearth.debug.showSystemMessages'

export const useAppStore = create<AppState>((set) => ({
  isRegistered: false,
  humanUserID: null,
  organizationID: null,
  ioDeviceID: null,
  email: null,
  sessionEndedReason: null,

  isSubscribed: false,
  subscribedProductID: null,
  subscribedPlatform: null,
  subscribedExpiresAt: null,
  hasActiveOffer: false,
  offerError: null,

  connectionStatus: 'disconnected',

  agentInstancesByOrg: {},
  organizationPositionsByOrg: {},
  hostsByOrg: {},
  rulesByOrg: {},
  chatRoomsByOrg: {},
  scheduledTriggersByOrg: {},
  provisioningProposalsByOrg: {},

  agentInstances: EMPTY,
  organizationPositions: EMPTY,
  hosts: EMPTY,
  rules: EMPTY,
  scheduledTriggers: EMPTY,
  provisioningProposals: EMPTY,
  availableOrganizations: [],
  hasLoadedOrganizations: false,
  ownedOrgCap: 1,
  resourceConnections: [],
  pluginInstalls: [],
  ioDevices: [],
  displayScreens: [],
  workingDirectories: [],
  organizationMembers: [],
  invites: [],
  myInvites: [],
  agentJobDescriptions: [],
  secrets: [],
  toasts: [],
  initiatorNotices: [],
  chatRooms: EMPTY,
  chatMessages: {},
  chatUnreadCounts: {},
  attentionByOrg: {},
  setAttentionByOrg: (m) => set({ attentionByOrg: m }),
  attentionRooms: [],
  setAttentionRooms: (rooms) => set({ attentionRooms: rooms }),
  attentionThreads: [],
  setAttentionThreads: (threads) => set({ attentionThreads: threads }),
  attentionSuggestions: [],
  setAttentionSuggestions: (items) => set({ attentionSuggestions: items }),
  chatNotifModes: {},

  pendingRequests: [],
  unreadRoomCount: 0,
  setUnreadRoomCount: (n) =>
    set((s) => (s.unreadRoomCount === n ? {} : { unreadRoomCount: Math.max(0, n) })),
  unreadThreadCount: 0,
  setUnreadThreadCount: (n) =>
    set((s) => (s.unreadThreadCount === n ? {} : { unreadThreadCount: Math.max(0, n) })),
  pendingSuggestionCount: 0,
  setPendingSuggestionCount: (n) =>
    set((s) => (s.pendingSuggestionCount === n ? {} : { pendingSuggestionCount: Math.max(0, n) })),
  transcripts: {},
  voiceSessionAgentID: null,
  setVoiceSessionAgent: (agentInstanceID) =>
    set({ voiceSessionAgentID: agentInstanceID }),
  positionHistory: {},
  historyLoadedFor: {},

  sheets: [],
  navStack: [],
  selectedAgentInstanceID: null,
  relayInputDrafts: {},
  showSystemMessages: localStorage.getItem(SHOW_SYSTEM_MESSAGES_KEY) === 'true',

  tempAgentDefaults: null,
  setTempAgentDefaultsStore: (tempAgentDefaults) => set({ tempAgentDefaults }),

  pendingTempSpawn: false,
  tempSpawnError: null,
  pendingPushPositionID: null,
  invitationsLinkPending: false,
  pendingPairCode: null,
  invitePrefillEmail: null,
  singleAskRequestID: null,
  announcements: [],
  lastPushEventAt: null,

  setRegistered: ({ humanUserID, organizationID, ioDeviceID, email }) =>
    set({ isRegistered: true, humanUserID, organizationID, ioDeviceID, email, sessionEndedReason: null }),
  setSessionEndedReason: (r) => set({ sessionEndedReason: r }),
  setShowSystemMessages: (v) => {
    localStorage.setItem(SHOW_SYSTEM_MESSAGES_KEY, v ? 'true' : 'false')
    set({ showSystemMessages: v })
  },
  clearRegistration: () =>
    set({
      isRegistered: false,
      humanUserID: null,
      organizationID: null,
      ioDeviceID: null,
      email: null,
      availableOrganizations: [],
      hasLoadedOrganizations: false,
    }),
  setConnectionStatus: (connectionStatus) => set({ connectionStatus }),
  setSubscribed: (isSubscribed) => set({ isSubscribed }),
  setSubscribedProductID: (subscribedProductID) => set({ subscribedProductID }),
  setSubscribedPlatform: (subscribedPlatform) => set({ subscribedPlatform }),
  setSubscribedExpiresAt: (subscribedExpiresAt) => set({ subscribedExpiresAt }),
  setActiveOffer: (hasActiveOffer) => set({ hasActiveOffer }),
  setOfferError: (offerError) => set({ offerError }),

  // Each household-scoped setter files the list under its household, then
  // recomputes the projection. When orgID names a household other than the
  // selected one the bucket updates and the projection doesn't move — the
  // frame is retained (so switching to it renders from cache) but invisible.
  //
  // An empty orgID means the server had no household context to scope by.
  // File nothing: there's no bucket to name, and the projection is already
  // empty because organizationID is unset too.
  replaceAgentInstances: (orgID, agentInstances) =>
    set((s) => {
      if (!orgID) return {}
      const agentInstancesByOrg = { ...s.agentInstancesByOrg, [orgID]: agentInstances }
      return { agentInstancesByOrg, ...reproject({ ...s, agentInstancesByOrg }) }
    }),
  replacePositions: (orgID, organizationPositions) =>
    set((s) => {
      if (!orgID) return {}
      const organizationPositionsByOrg = { ...s.organizationPositionsByOrg, [orgID]: organizationPositions }
      return { organizationPositionsByOrg, ...reproject({ ...s, organizationPositionsByOrg }) }
    }),
  replaceHosts: (orgID, hosts) =>
    set((s) => {
      if (!orgID) return {}
      const hostsByOrg = { ...s.hostsByOrg, [orgID]: hosts }
      return { hostsByOrg, ...reproject({ ...s, hostsByOrg }) }
    }),
  replaceRules: (orgID, rules) =>
    set((s) => {
      if (!orgID) return {}
      const rulesByOrg = { ...s.rulesByOrg, [orgID]: rules }
      return { rulesByOrg, ...reproject({ ...s, rulesByOrg }) }
    }),
  clearHouseholdState: () =>
    set({
      agentInstancesByOrg: {},
      organizationPositionsByOrg: {},
      hostsByOrg: {},
      rulesByOrg: {},
      chatRoomsByOrg: {},
      scheduledTriggersByOrg: {},
      provisioningProposalsByOrg: {},
      agentInstances: EMPTY,
      organizationPositions: EMPTY,
      hosts: EMPTY,
      rules: EMPTY,
      chatRooms: EMPTY,
      scheduledTriggers: EMPTY,
      provisioningProposals: EMPTY,
    }),
  replaceResourceConnections: (resourceConnections) => set({ resourceConnections }),
  replacePluginInstalls: (pluginInstalls) => set({ pluginInstalls }),
  replaceIODevices: (ioDevices) => set({ ioDevices }),
  replaceDisplayScreens: (displayScreens) => set({ displayScreens }),
  replaceWorkingDirectories: (workingDirectories) => set({ workingDirectories }),
  replaceOrganizationMembers: (organizationMembers) => set({ organizationMembers }),
  replaceInvites: (invites) => set({ invites }),
  replaceMyInvites: (myInvites) => set({ myInvites }),
  replaceAgentJobDescriptions: (agentJobDescriptions) => set({ agentJobDescriptions }),
  replaceSecrets: (secrets) => set({ secrets }),
  replaceChatRooms: (orgID, chatRooms) =>
    set((s) => {
      if (!orgID) return {}
      const chatRoomsByOrg = { ...s.chatRoomsByOrg, [orgID]: chatRooms }
      // Adopt the server's per-room unread counts as authoritative for exactly
      // the rooms in this frame, leaving other orgs' entries alone. The server
      // derives unread from the per-human read watermark, so this survives a
      // reload and stays consistent phone↔tablet — and it's what makes a room's
      // pip and its contribution to the app-icon badge agree. (Older servers
      // omit unread_count; those rooms fall back to the optimistic client
      // counter incremented on live messages.)
      const chatUnreadCounts = { ...s.chatUnreadCounts }
      for (const r of chatRooms) {
        if (typeof r.unread_count === 'number') {
          if (r.unread_count > 0) chatUnreadCounts[r.id] = r.unread_count
          else delete chatUnreadCounts[r.id]
        }
      }
      return { chatRoomsByOrg, chatUnreadCounts, ...reproject({ ...s, chatRoomsByOrg }) }
    }),
  replaceScheduledTriggers: (orgID, scheduledTriggers) =>
    set((s) => {
      if (!orgID) return {}
      const scheduledTriggersByOrg = { ...s.scheduledTriggersByOrg, [orgID]: scheduledTriggers }
      return { scheduledTriggersByOrg, ...reproject({ ...s, scheduledTriggersByOrg }) }
    }),
  replaceProvisioningProposals: (orgID, proposals) =>
    set((s) => {
      if (!orgID) return {}
      const provisioningProposalsByOrg = { ...s.provisioningProposalsByOrg, [orgID]: proposals }
      return { provisioningProposalsByOrg, ...reproject({ ...s, provisioningProposalsByOrg }) }
    }),
  // Merge ONE proposal into the review queue (from a provisioning_proposal_created
  // frame). Robust against a lost wsRequest response / missed list push: a still-
  // open proposal is upserted; a resolved one (applied/declined) drops out.
  upsertProvisioningProposal: (orgID, proposal) =>
    set((s) => {
      if (!orgID || !proposal?.id) return {}
      const cur = s.provisioningProposalsByOrg[orgID] ?? []
      const without = cur.filter((p) => p.id !== proposal.id)
      const next =
        proposal.status && proposal.status !== 'proposed' ? without : [proposal, ...without]
      const provisioningProposalsByOrg = { ...s.provisioningProposalsByOrg, [orgID]: next }
      return { provisioningProposalsByOrg, ...reproject({ ...s, provisioningProposalsByOrg }) }
    }),
  upsertChatMessages: (roomID, msgs) =>
    set((s) => {
      if (msgs.length === 0) return {}
      const byID = new Map(msgs.map((m) => [m.id, m]))
      const existing = s.chatMessages[roomID] ?? []
      const held = new Set(existing.map((m) => m.id))
      const merged = existing.map((m) => byID.get(m.id) ?? m)
      const fresh = msgs.filter((m) => !held.has(m.id))
      return {
        chatMessages: {
          ...s.chatMessages,
          [roomID]: [...merged, ...fresh].sort(chatMsgLess),
        },
      }
    }),
  appendChatMessages: (roomID, msgs) =>
    set((s) => {
      const existing = s.chatMessages[roomID] ?? []
      const seen = new Set(existing.map((m) => m.id))
      const fresh = msgs.filter((m) => !seen.has(m.id))
      if (fresh.length === 0) return {}
      return {
        chatMessages: {
          ...s.chatMessages,
          [roomID]: [...existing, ...fresh].sort(chatMsgLess),
        },
      }
    }),
  prependChatMessages: (roomID, msgs) =>
    set((s) => {
      const existing = s.chatMessages[roomID] ?? []
      const seen = new Set(existing.map((m) => m.id))
      const fresh = msgs.filter((m) => !seen.has(m.id))
      if (fresh.length === 0) return {}
      return {
        chatMessages: {
          ...s.chatMessages,
          [roomID]: [...fresh, ...existing].sort(chatMsgLess),
        },
      }
    }),
  updateChatMessage: (roomID, msgID, patch) =>
    set((s) => {
      const existing = s.chatMessages[roomID]
      if (!existing) return {}
      const idx = existing.findIndex((m) => m.id === msgID)
      if (idx === -1) return {}
      const updated = [...existing]
      updated[idx] = { ...updated[idx], ...patch }
      return { chatMessages: { ...s.chatMessages, [roomID]: updated } }
    }),
  confirmOptimisticMessage: (roomID, tempID, real) =>
    set((s) => {
      const existing = s.chatMessages[roomID] ?? []
      const withoutTemp = existing.filter((m) => m.id !== tempID)
      // Broadcast may have already delivered the authoritative row (same real id)
      // before this ack — in that case just drop the placeholder. Otherwise place
      // the real row so there's no flicker/gap before the broadcast lands.
      const list = withoutTemp.some((m) => m.id === real.id)
        ? withoutTemp
        : [...withoutTemp, real].sort(chatMsgLess)
      return { chatMessages: { ...s.chatMessages, [roomID]: list } }
    }),
  removeChatMessage: (roomID, msgID) =>
    set((s) => {
      const existing = s.chatMessages[roomID]
      if (!existing) return {}
      const filtered = existing.filter((m) => m.id !== msgID)
      if (filtered.length === existing.length) return {}
      return { chatMessages: { ...s.chatMessages, [roomID]: filtered } }
    }),
  incrementChatUnread: (roomID) =>
    set((s) => ({
      chatUnreadCounts: {
        ...s.chatUnreadCounts,
        [roomID]: (s.chatUnreadCounts[roomID] ?? 0) + 1,
      },
    })),
  clearChatUnread: (roomID) =>
    set((s) => {
      if (!s.chatUnreadCounts[roomID]) return {}
      const next = { ...s.chatUnreadCounts }
      delete next[roomID]
      return { chatUnreadCounts: next }
    }),
  resetChatUnreadCounts: () => set({ chatUnreadCounts: {} }),
  setChatNotifMode: (roomID, mode) =>
    set((s) => ({ chatNotifModes: { ...s.chatNotifModes, [roomID]: mode } })),
  pushToast: (text, kind = 'info') => {
    // Light per-call uniqueness so two near-simultaneous toasts
    // (e.g. invite-accept fans out member_added + member_joined push
    // at the same instant) get distinct dom keys + dismiss timers.
    const id =
      typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `t-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
    const toast: Toast = {
      id,
      text,
      kind,
      // ~4 seconds visible. Toasts component prunes expired entries
      // on its tick.
      expiresAt: Date.now() + 4000,
    }
    set((s) => ({ toasts: [...s.toasts, toast] }))
  },
  dismissToast: (id) =>
    set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) })),
  replaceOrganizations: (availableOrganizations, ownedOrgCap) => {
    // Whatever the server says is the current org wins — keeps the
    // store's organizationID (used by every CRUD/wizard call) in sync
    // with the device's actual current_organization across switches,
    // invite-accepts, and reconnects.
    const current = availableOrganizations.find((o) => o.is_current)
    const prev = useAppStore.getState().availableOrganizations
    const prevCurrent = prev.find((o) => o.is_current)
    const justBecamePro =
      !!current?.is_pro && !(prevCurrent?.is_pro ?? false)
    set((s) => {
      const organizationID = current ? current.id : s.organizationID
      // Evict buckets for households this user is no longer in. The list
      // is authoritative membership, so anything absent from it is either
      // a household they left or one they were removed from — either way
      // its cached lists are unreachable and would otherwise accumulate
      // for the life of the session.
      const live = new Set(availableOrganizations.map((o) => o.id))
      const evicted: Record<string, unknown> = {}
      for (const [, storeKey] of HOUSEHOLD_SLICES) {
        const bucket = s[storeKey] as Record<string, unknown>
        const keep = Object.keys(bucket).filter((id) => live.has(id))
        if (keep.length !== Object.keys(bucket).length) {
          evicted[storeKey] = Object.fromEntries(keep.map((id) => [id, bucket[id]]))
        }
      }
      const next = {
        ...s,
        ...evicted,
        organizationID,
      } as AppState
      return {
        availableOrganizations,
        hasLoadedOrganizations: true,
        organizationID,
        ...evicted,
        // Switching households re-points the projection at the new
        // household's cached lists immediately — the server's re-push
        // then refreshes them rather than populating from nothing.
        ...reproject(next),
        ...(ownedOrgCap !== undefined ? { ownedOrgCap } : {}),
      }
    })
    if (justBecamePro && bridge.isNative) {
      // The current org just gained Pro (offer code redeemed, sub
      // claimed, etc). On native, push permission is system-managed
      // and auto-requesting here is quiet — kick the permission prompt
      // + APNs registration so the user starts receiving notifications
      // without further action.
      //
      // Web is deliberately excluded: browser Notification API
      // requires a user gesture (Chrome 80+) to surface a prompt,
      // so auto-firing here would silently no-op in most cases.
      // Worse, on browsers that do honor it, the user gets an
      // unexpected permission prompt with no way to recover from
      // "deny" except their browser site settings. Web users enable
      // via the explicit "Enable browser notifications" button in
      // About — that's the user-gesture path the browser API wants.
      void bridge.call('apns.registerForPush').catch(() => {})
    }
  },

  enqueueRequest: (r) =>
    set((s) => {
      // Idempotent on id — concurrent fetches and WS frames can both
      // deliver the same request, and each consumer was previously
      // doing its own existingIDs snapshot before enqueueing in a
      // loop. Two consumers seeing the same pre-state would both
      // enqueue, inflating the banner count. Dedupe at the store so
      // no caller has to coordinate. Replace the existing entry so
      // newer denormalized fields (agent_name, position_name, etc.)
      // from a fresh fetch override stale ones.
      const idx = s.pendingRequests.findIndex((p) => p.id === r.id)
      if (idx < 0) {
        return { pendingRequests: [...s.pendingRequests, r] }
      }
      const next = s.pendingRequests.slice()
      next[idx] = r
      return { pendingRequests: next }
    }),
  dequeueRequest: (id) =>
    set((s) => ({
      pendingRequests: s.pendingRequests.filter((r) => r.id !== id),
    })),
  retainPendingRequests: (ids) =>
    set((s) => {
      const keep = new Set(ids)
      const next = s.pendingRequests.filter((r) => keep.has(r.id))
      // Identity-stable when nothing was dropped: the notification-reconcile
      // subscriber keys off pendingRequests changing, and a foreground
      // refresh that found nothing stale shouldn't churn the badge.
      if (next.length === s.pendingRequests.length) return {}
      return { pendingRequests: next }
    }),

  upsertInitiatorNotice: (n) =>
    set((s) => {
      const idx = s.initiatorNotices.findIndex((x) => x.requestId === n.requestId)
      if (idx < 0) return { initiatorNotices: [...s.initiatorNotices, n] }
      const next = s.initiatorNotices.slice()
      next[idx] = n
      return { initiatorNotices: next }
    }),
  settleInitiatorNotice: (requestId, status, body) =>
    set((s) => {
      const idx = s.initiatorNotices.findIndex((x) => x.requestId === requestId)
      if (idx < 0) return {}
      const next = s.initiatorNotices.slice()
      next[idx] = { ...next[idx], status, body: body || next[idx].body }
      return { initiatorNotices: next }
    }),
  dismissInitiatorNotice: (requestId) =>
    set((s) => ({
      initiatorNotices: s.initiatorNotices.filter((x) => x.requestId !== requestId),
    })),

  appendTranscript: (entries) =>
    set((s) => {
      // Bucket incoming entries by agent, then dedup + prune each bucket
      // independently. Entries with no agent id are dropped — a malformed
      // frame must not land in a shared bucket (ws.ts already guards this,
      // but keep the store self-defending).
      const byAgent = new Map<string, TranscriptEntry[]>()
      for (const e of entries) {
        if (!e.ai_agent_instance_id) continue
        const arr = byAgent.get(e.ai_agent_instance_id)
        if (arr) arr.push(e)
        else byAgent.set(e.ai_agent_instance_id, [e])
      }
      if (byAgent.size === 0) return {}
      let changed = false
      const next = { ...s.transcripts }
      for (const [agentID, incoming] of byAgent) {
        const existing = s.transcripts[agentID] ?? []
        const seen = new Set(existing.map((e) => e.id))
        const fresh = incoming.filter((e) => !seen.has(e.id))
        if (fresh.length === 0) continue
        const merged = [...existing, ...fresh]
        // When every entry in the bucket carries a stamped ordering key, sort
        // by (seq,sub,block) so live and replayed entries land in true
        // conversation order and the newest-N prune keeps the newest by that
        // order. A bucket with any un-stamped entry (older daemon) keeps
        // arrival order — stamped vs un-stamped aren't comparable.
        const ordered = merged.every((e) => typeof e.seq === 'number')
          ? merged.slice().sort(transcriptSeqLess)
          : merged
        next[agentID] =
          ordered.length > TRANSCRIPT_MAX
            ? ordered.slice(ordered.length - TRANSCRIPT_MAX)
            : ordered
        changed = true
      }
      // Reference-stable no-op when nothing was fresh, so Zustand skips render.
      return changed ? { transcripts: next } : {}
    }),
  patchTranscriptEntry: (aiAgentInstanceID, id, patch) =>
    set((s) => {
      const bucket = s.transcripts[aiAgentInstanceID]
      if (!bucket) return {}
      const idx = bucket.findIndex((e) => e.id === id)
      if (idx < 0) return {}
      const next = bucket.slice()
      next[idx] = { ...next[idx], ...patch }
      return { transcripts: { ...s.transcripts, [aiAgentInstanceID]: next } }
    }),
  markTranscriptUndeliverable: (aiAgentInstanceID, mid, reason) =>
    set((s) => {
      const bucket = s.transcripts[aiAgentInstanceID]
      if (!bucket || !mid) return {}
      let changed = false
      const next = bucket.map((e) => {
        if (e.mid !== mid || e.send_state === 'undelivered') return e
        changed = true
        return { ...e, send_state: 'undelivered' as const, undeliverable_reason: reason }
      })
      // Reference-stable no-op when the row is already gone or already marked —
      // a late/duplicate report must not churn the transcript.
      if (!changed) return {}
      return { transcripts: { ...s.transcripts, [aiAgentInstanceID]: next } }
    }),
  clearTranscript: (agentInstanceID) =>
    set((s) => {
      if (!agentInstanceID) return { transcripts: {} }
      if (!s.transcripts[agentInstanceID]) return {}
      const next = { ...s.transcripts }
      delete next[agentInstanceID]
      return { transcripts: next }
    }),

  appendHistory: (item) =>
    set((s) => {
      const bucket = s.positionHistory[item.organization_position_id] ?? []
      // Dedupe: live WS events can race with a just-completed query response.
      if (bucket.some((h) => h.id === item.id)) return {}
      return {
        positionHistory: {
          ...s.positionHistory,
          [item.organization_position_id]: [...bucket, item],
        },
      }
    }),

  mergeHistory: (positionID, items) =>
    set((s) => {
      if (items.length === 0) return {}
      const existing = s.positionHistory[positionID] ?? []
      const seen = new Set(existing.map((h) => h.id))
      const fresh = items.filter((h) => !seen.has(h.id))
      if (fresh.length === 0) return {}
      return {
        positionHistory: {
          ...s.positionHistory,
          [positionID]: [...existing, ...fresh],
        },
      }
    }),

  markHistoryLoaded: (positionID) =>
    set((s) => ({
      historyLoadedFor: { ...s.historyLoadedFor, [positionID]: true },
    })),

  pushSheet: (sheet) => set((s) => ({ sheets: [...s.sheets, sheet] })),
  popSheet: () => set((s) => ({ sheets: s.sheets.slice(0, -1) })),
  pushNav: (entry) => set((s) => ({ navStack: [...s.navStack, entry] })),
  popNav: () => set((s) => ({ navStack: s.navStack.slice(0, -1) })),
  setSelectedAgentInstance: (selectedAgentInstanceID) =>
    set({ selectedAgentInstanceID }),
  setRelayInputDraft: (aiAgentInstanceID, t) =>
    set((s) => {
      // Drop the entry on empty so the map doesn't accumulate keys for
      // every agent the user has ever opened.
      const next = { ...s.relayInputDrafts }
      if (t === '') delete next[aiAgentInstanceID]
      else next[aiAgentInstanceID] = t
      return { relayInputDrafts: next }
    }),

  markPendingTempSpawn: () => set({ pendingTempSpawn: true, tempSpawnError: null }),
  clearPendingTempSpawn: () => set({ pendingTempSpawn: false }),
  setTempSpawnError: (tempSpawnError) => set({ tempSpawnError }),

  setPendingPushPositionID: (pendingPushPositionID) =>
    set({ pendingPushPositionID }),

  setInvitationsLinkPending: (invitationsLinkPending) => set({ invitationsLinkPending }),
  setPendingPairCode: (pendingPairCode) => set({ pendingPairCode }),
  setInvitePrefillEmail: (invitePrefillEmail) => set({ invitePrefillEmail }),

  setSingleAskRequestID: (singleAskRequestID) => set({ singleAskRequestID }),

  setAnnouncements: (announcements) => set({ announcements }),
  enqueueAnnouncement: (a) =>
    set((s) =>
      s.announcements.some((x) => x.id === a.id)
        ? s
        : { announcements: [...s.announcements, a] },
    ),
  removeAnnouncement: (id) =>
    set((s) => ({
      announcements: s.announcements.filter((a) => a.id !== id),
    })),
  markPushEvent: () => set({ lastPushEventAt: Date.now() }),
}))
